import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { LANG_META, SUPPORTED_LANGS, type Lang } from "@/i18n";
import ogDefault from "@/assets/og-default.jpg";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  schema?: object | object[];
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
}

const SITE_URL = "https://assi.org.il";

export const SEO = ({ title, description, canonical, schema, image, type = "website", noindex }: SEOProps) => {
  const { i18n } = useTranslation();
  const lang = (SUPPORTED_LANGS as readonly string[]).includes(i18n.language)
    ? (i18n.language as Lang) : "he";
  const meta = LANG_META[lang];

  const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
  const url = canonical ?? `${SITE_URL}${path}`;
  const ogImage = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}${ogDefault}`;

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];

  // Build hreflang alternates
  const pathWithoutLang = path.replace(/^\/(he|ar|en)/, "") || "/";

  return (
    <Helmet>
      <html lang={meta.htmlLang} dir={meta.dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {SUPPORTED_LANGS.map(l => (
        <link
          key={l}
          rel="alternate"
          hrefLang={LANG_META[l].htmlLang}
          href={`${SITE_URL}/${l}${pathWithoutLang === "/" ? "" : pathWithoutLang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/he${pathWithoutLang === "/" ? "" : pathWithoutLang}`} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={meta.ogLocale} />
      <meta property="og:site_name" content="משרד עו״ד ניזאר עאסי" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};
